/**
 * Utility functions for article page functionality
 */

export interface TOCItem {
  id: string;
  text: string;
  level: number;
  children?: TOCItem[];
}

/**
 * Calculate reading time based on word count
 * Average reading speed: 200-250 words per minute
 */
export const calculateReadingTime = (content: string): number => {
  const text = content.replace(/<[^>]*>/g, ''); // Strip HTML tags
  const words = text.trim().split(/\s+/).length;
  const wordsPerMinute = 225;
  return Math.ceil(words / wordsPerMinute);
};

/**
 * Generate table of contents from HTML content.
 *
 * Honors hand-authored ids on headings (e.g. <h2 id="macbook-air-m4">) so
 * articles with curated jump-link anchors keep them. Falls back to
 * heading-{index} only when the heading element has no id of its own,
 * preserving the prior behavior for legacy articles.
 */
export const generateTableOfContents = (content: string): TOCItem[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const headings = doc.querySelectorAll('h1, h2, h3, h4');

  const toc: TOCItem[] = [];
  const stack: TOCItem[] = [];

  headings.forEach((heading, index) => {
    const level = parseInt(heading.tagName.substring(1));
    const text = heading.textContent || '';
    const existingId = (heading as HTMLElement).id;
    const id = existingId || `heading-${index}`;

    const item: TOCItem = {
      id,
      text,
      level,
      children: []
    };

    // Find the correct parent level
    while (stack.length > 0 && stack[stack.length - 1].level >= level) {
      stack.pop();
    }

    if (stack.length === 0) {
      toc.push(item);
    } else {
      const parent = stack[stack.length - 1];
      if (!parent.children) parent.children = [];
      parent.children.push(item);
    }

    stack.push(item);
  });

  return toc;
};

/**
 * Add IDs to headings in HTML content for TOC navigation.
 *
 * Only assigns heading-{index} when the heading has no id already. This
 * preserves curated jump-link targets (e.g. <h2 id="macbook-air-m4">) that
 * an editor pasted into the article. The old unconditional overwrite broke
 * the table-of-contents on every listicle that depended on stable anchors.
 */
export const addHeadingIds = (content: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const headings = doc.querySelectorAll('h1, h2, h3, h4');

  headings.forEach((heading, index) => {
    if (!heading.id) {
      heading.setAttribute('id', `heading-${index}`);
    }
  });

  return doc.body.innerHTML;
};

/**
 * Calculate scroll progress percentage
 */
export const calculateScrollProgress = (): number => {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
  return Math.min(100, Math.max(0, (scrollTop / scrollHeight) * 100));
};

/**
 * Format view count with K/M suffix
 */
export const formatViewCount = (count: number): string => {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}k`;
  }
  return count.toString();
};

/**
 * Get time remaining to read based on scroll position
 */
export const getTimeRemaining = (totalTime: number, scrollProgress: number): number => {
  const remaining = totalTime * (1 - scrollProgress / 100);
  return Math.max(0, Math.ceil(remaining));
};

/**
 * Save reading progress to localStorage
 */
export const saveReadingProgress = (articleId: string, progress: number): void => {
  const key = `reading_progress_${articleId}`;
  localStorage.setItem(key, progress.toString());
};

/**
 * Get saved reading progress from localStorage
 */
export const getReadingProgress = (articleId: string): number => {
  const key = `reading_progress_${articleId}`;
  const saved = localStorage.getItem(key);
  return saved ? parseFloat(saved) : 0;
};

/**
 * Track article view
 */
export const trackArticleView = async (articleSlug: string): Promise<void> => {
  try {
    await fetch(`/api/articles/${articleSlug}/view`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    });
  } catch (error) {
    console.error('Failed to track view:', error);
  }
};

/**
 * Copy code to clipboard
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy:', error);
    return false;
  }
};